import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import '@adornis/file-utils/client/x-file-input.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import { FederalStatesGermany } from '@ils/static/enums.js';
import { html, nothing, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { firstValueFrom, timer } from 'rxjs';
import { ILSProfile, ILSUser } from '../../db/ils-user.js';

/**
 * @element ils-signup-missing
 */
@customElement('ils-signup-missing')
export class IlsSignupMissing extends ChemistryLitElement {
  @property({ attribute: false }) user = new RXController<Maybe<ILSUser>>(this, null);

  @state() private _missingProperties: string[] | null = null;

  @state() private _form = new FormController(this, this.user.observable, {
    success: async user => {
      try {
        await XDialog.waitFor(Promise.all([user.save(), firstValueFrom(timer(250))]));
        void XSnackbar.show('Nutzer wurde erfolgreicht aktualisiert');
        this._missingProperties = user.missingProperties();
      } catch (e) {
        logger.error({ err: e }, 'Es gab ein Problem beim Speichern des Nutzers');
        void XDialog.alert(
          'Es gab ein Problem beim Speichern des Nutzers. Versuchen Sie es später erneut oder wenden Sie sich an den Support',
        );
      }
    },
  });

  // * we need to remember all fields until we save the user, so they dont disappear when we re-check for missing fields with dependencies on the ones we already selected
  // * since the b2cert depends on the targetFederalState we remove it if the state is missing or the special case Bayern is selected as we dont need it there
  private _updateMissingProperties() {
    const currentlyMissing = this._missingProperties ?? [];
    const exactMissing = this.user.value?.missingProperties() ?? [];
    const newMissing = Array.from(new Set(currentlyMissing.concat(exactMissing)));
    const b2Index = newMissing.indexOf('profile.b2Certificate');
    if (
      b2Index !== -1 &&
      (exactMissing.includes('profile.targetFederalState') ||
        this.user.value?.profile?.targetFederalState === FederalStatesGermany.BAYERN)
    )
      newMissing.splice(b2Index, 1);
    this._missingProperties = newMissing;
  }

  override willUpdate(changedProperties: PropertyValues) {
    super.willUpdate(changedProperties);

    if (!this._form.document) return;
    if (!this._form.document.profile) this._form.document.profile = new ILSProfile({});
    this._updateMissingProperties();
  }

  override render() {
    return html`
      <x-flex space="md">
        ${this._missingProperties?.length
          ? html`
              <x-text tone="warning">
                Es gibt zusätzliche Informationen die Sie für diese Prüfung angeben müssen!
              </x-text>
            `
          : nothing}
        ${(
          [
            [
              'profile.b2Certificate',
              () => html`
                <x-flex space="md">
                  <x-flex space="sm">
                    <x-h2> B2-Zertifikat </x-h2>
                    <x-text tone="subtle">
                      Laden Sie hier eine Kopie Ihres B2-Zertifikats hoch. Das Zertifikat muss am Tag der Prüfung im
                      Original vorgelegt werden.
                    </x-text>
                  </x-flex>
                  <x-file-input
                    ${this._form.field('profile', 'b2Certificate')}
                    placeholder="B2 Zertifikat"
                  ></x-file-input>
                </x-flex>
              `,
            ],
            [
              'profile.targetFederalState',
              () => html`
                <x-flex space="md">
                  <x-dropdown-selection
                    placeholder="In welchem Bundesland wollen Sie ihren Approbationsantrag stellen?*"
                    .selectables=${Object.values(FederalStatesGermany)}
                    ${this._form.field('profile', 'targetFederalState')}
                    @value-picked=${() => this.requestUpdate()}
                  >
                  </x-dropdown-selection>
                </x-flex>
              `,
            ],
          ] as const
        )
          .filter(([missingProperty]) => this._missingProperties?.includes(missingProperty))
          .map(([_, templateFn]) => templateFn())}
        ${this._missingProperties?.length
          ? html`
              <x-button mode="outline" ${css({ alignSelf: 'end' })} @click=${() => this._form.validate()}>
                Nutzer speichern
              </x-button>
            `
          : nothing}
      </x-flex>
    `;
  }
}
